import { useMemo } from "react";
import { ColDef } from "ag-grid-community";
import { useLoaderData } from "react-router-dom";
import { ParkerDetails } from "./Data/ApiTypes";
import { showMoneyShort } from "./Data/Money";
import { LocalDateTime, ZoneId } from "@js-joda/core";
import { DataGrid } from "./Controls/DataGrid";
import { PageBar } from "./Controls/PageBar";
import { gridOneCol } from "./Data/Common";
import { Col, Container, Row } from "react-bootstrap";

export function ReservationsPage() {
    const parker = useLoaderData() as ParkerDetails;

    const toJsDate = (ldt: LocalDateTime): Date => {
        const zdt = ldt.atZone(ZoneId.SYSTEM);
        return new Date(zdt.toInstant().toEpochMilli());
    };

    const getStatus = (start: LocalDateTime, end: LocalDateTime) => {
        const now = new Date();
        const startDate = toJsDate(start);
        const endDate = toJsDate(end);
        if (endDate < now) return 'Expired';
        if (startDate > now) return 'Upcoming';
        return 'In Progress';
    };

    const getDuration = (seconds: number) => {
        const hours = seconds / 3600;
        const formatted = Number.isInteger(hours) ? hours : hours.toFixed(1);
        const label = hours === 1 ? "hr" : "hrs";
        return `${formatted} ${label}`;
    };

    const showDateTime = (date: Date) => 
      date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).replace(',', '');


    const columns = useMemo<ColDef[]>(() => [
        { field: "rateProgram.name", headerName: "Rate", sortable: true, filter: true },
        { 
            field: "start", 
            headerName: "Check-In", 
            sortable: true, 
            filter: true,
            valueFormatter: p => showDateTime(toJsDate(p.value))
        },
        { 
            field: "end", 
            headerName: "Check-Out", 
            sortable: true, 
            filter: true,
            valueFormatter: p => showDateTime(toJsDate(p.value))
        },
        { 
            headerName: "Vehicle", 
            valueGetter: () => "Not Specified"
        },
        {
        headerName: "Status",
        valueGetter: (p) => getStatus(p.data.start, p.data.end),
        cellRenderer: (params: any) => {
            const status = params.value;
            let color = "";

            switch (status) {
            case "Expired":
                color = "gray";
                break;
            case "Upcoming":
            case "In Progress":
                color = "green";
                break;
            }

            return (
            <span style={{ color, fontWeight: 600 }}>
                {status}
            </span>
            );
        },
        },
        { 
            headerName: "For", 
            valueGetter: p => getDuration(p.data.duration.seconds())
        },
        { 
            field: "price", 
            headerName: "Price", 
            type: "rightAligned", 
            valueFormatter: p => showMoneyShort(p.value) 
        }
    ], []);

    return (
        <Container fluid className="h-100">
            <Row className="h-100">
                <Col style={gridOneCol("max-content 1fr 5px")}>
                    <PageBar title="Reservations" />
                    <div style={{ height: "100%", width: "100%" }}>
                        <DataGrid
                            rowData={[...parker.reservations.sort((a, b) => new Date(toJsDate(b.end)).getTime() - new Date(toJsDate(a.end)).getTime())]}
                            columnDefs={columns}
                        />
                    </div>
                </Col>
            </Row>
        </Container>
    );
}
